import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { FcGoogle } from "react-icons/fc";
import Button from "./common/Button";
import Input from "./common/Input";
import Loading from "./common/Loading";
import bgImg from "../pnai-bg.jpg";
import Logo from "./common/Logo";

type InviteInfo = {
  newEmail: string;
  currentEmail: string;
  name: string | null;
  hasAgency: boolean;
};

const ERROR_MESSAGES: Record<string, string> = {
  invalid_token: "This email change link is no longer valid.",
  oauth_not_allowed:
    "Google sign-in isn't available for agency accounts. Set a password instead.",
  email_mismatch:
    "The Google account you used doesn't match the email this invite was sent to. Sign in with the correct Google account or set a password instead.",
};

const AccountTransfer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const errorCode = searchParams.get("error");

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errorState, setErrorState] = useState(false);

  useEffect(() => {
    if (!token) {
      setLinkError("This email change link is missing a token.");
      setIsLoading(false);
      return;
    }
    const validate = async () => {
      try {
        const response = await axios.get(
          `/api/auth/email-change/validate?token=${encodeURIComponent(token)}`
        );
        setInvite({
          newEmail: response.data.newEmail,
          currentEmail: response.data.currentEmail,
          name: response.data.name,
          hasAgency: response.data.hasAgency,
        });
      } catch (error: any) {
        setLinkError(
          error.response?.data?.error || "This email change link is no longer valid."
        );
      } finally {
        setIsLoading(false);
      }
    };
    validate();
  }, [token]);

  useEffect(() => {
    if (errorCode && ERROR_MESSAGES[errorCode]) {
      toast.error(ERROR_MESSAGES[errorCode]);
    }
  }, [errorCode]);

  const validateForm = () => {
    const next: { password?: string; confirmPassword?: string } = {};
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/;
    if (!password) {
      next.password = "Password is required";
    } else if (!passwordRegex.test(password)) {
      next.password =
        "Password must be at least 12 characters and include upper, lower, number, and symbol";
    }
    if (!confirmPassword) {
      next.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      next.confirmPassword = "Passwords do not match";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setErrorState(true);
      return;
    }
    setIsSubmitting(true);
    setErrorState(false);
    try {
      await axios.post("/api/auth/email-change/accept-password", {
        token,
        password,
      });
      toast.success("Email changed. Sign in with your new email.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (error: any) {
      setErrorState(true);
      setErrors({
        overall: error.response?.data?.error || "Failed to change email",
      });
      setIsSubmitting(false);
    }
  };

  const handleGoogle = () => {
    if (!token) return;
    window.location.href = `/api/auth/email-change/google?token=${encodeURIComponent(token)}`;
  };

  if (isLoading) return <Loading />;

  if (linkError || !invite) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div
          className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${bgImg})` }}
        >
          <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
          <div className="w-3/5 z-10">
            <Logo className="w-full h-auto" />
          </div>
        </div>
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
          <div className="bg-base-100 p-8 rounded-xl shadow-md w-full max-w-sm">
            <h1 className="text-3xl mb-4 font-semibold text-red-600 dark:text-red-400">
              Link Invalid
            </h1>
            <p className="text-gray-700 dark:text-base-content/80 mb-6">{linkError}</p>
            <Button
              onClick={() => navigate("/login")}
              variant="primary"
              className="w-full"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div
        className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImg})` }}
      >
        <div className="absolute inset-0 bg-blue-50/70 dark:bg-blue-900/70"></div>
        <div className="w-3/5 z-10">
          <Logo className="w-full h-auto" />
        </div>
      </div>
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
        <div className="bg-base-100 p-8 rounded-xl shadow-md w-full max-w-sm">
          <h1 className="text-2xl mb-2 font-semibold">Change your email</h1>
          <p className="mb-6 text-sm text-gray-600 dark:text-base-content/70">
            Set a new password to move your account
            {invite.name ? ` for ${invite.name}` : ""} from{" "}
            <span className="font-semibold text-base-content">
              {invite.currentEmail}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-base-content">
              {invite.newEmail}
            </span>
            .
          </p>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Input
                title="New Email"
                type="email"
                id="newEmail"
                value={invite.newEmail}
                onChange={() => {}}
                disabled
                className="w-full"
              />
            </div>
            <div>
              <Input
                title="New Password"
                type="password"
                id="password"
                value={password}
                placeholder="At least 12 characters"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setPassword(e.target.value);
                }}
                className={`w-full ${errorState && "border-red-500"}`}
              />
              {errors.password && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>
            <div>
              <Input
                title="Confirm Password"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                placeholder="Confirm your new password"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setConfirmPassword(e.target.value);
                }}
                className={`w-full ${errorState && "border-red-500"}`}
              />
              {errors.confirmPassword && errorState && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.confirmPassword}
                </p>
              )}
              {errors.overall && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.overall}</p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Changing email...
                </>
              ) : (
                "Change Email"
              )}
            </Button>
          </form>

          {!invite.hasAgency && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-base-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-base-100 text-base-content/60">Or</span>
                </div>
              </div>
              <Button
                onClick={handleGoogle}
                variant="outline"
                className="w-full"
                type="button"
              >
                <FcGoogle className="w-5 h-5" />
                <span>Continue with Google</span>
              </Button>
              <p className="mt-2 text-[11px] text-base-content/60 text-center">
                Google sign-in must use the email address {invite.newEmail}.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountTransfer;
